import { useQuery } from '@apollo/client/react';
import { useCallback, useMemo, useState } from 'react';
import { useCreateModifyActions } from 'stores/useCreateModifyStore';
import { useCurrentUser, useIsWhiteLabelScoped, useUserActions } from 'stores/useUserStore';

import type Step from 'components/core/createModify/interfaces/step';
import type { Props as BaseDialogProps } from 'components/ui/dialogs/Dialog';

import { builderPermissions } from 'components/sections/createModify/builderPermissions';
import { LeadActivityBuilderStep } from 'components/sections/createModify/leadActivities/steps/interfaces';
import FilterableListDialog from 'components/ui/dialogs/FilterableListDialog';
import { AddIcon } from 'components/ui/icons';
import { BuilderType } from 'enums/builderType';
import { CreateModifyTiers } from 'enums/createModifyTiers';
import { CustomEntity } from 'enums/extendedEntityType';
import { FeatureBundleSet } from 'enums/featureBundle';
import { RoutePath } from 'enums/routePath';
import { ElementTestId } from 'enums/testing';
import { useCreateModify } from 'hooks/contexts/useCreateModify';
import { useRouter } from 'hooks/useRouter';
import { isFeatureBuilderEnabledForUser } from 'utils/featureBundleUtils';
import { translate } from 'utils/intlUtils';
import { fullGroupOrAboveScopes } from 'utils/permissionUtils';

import { userFeatureBundlesQuery } from 'store/api/graph/queries/user';
import {
  EntityType,
  type LeadActivityDetailsContainerQuery,
  LeadActivityStatus,
  UserScope,
} from 'store/client/graphql';

import { GREEN_500, NEUTRAL_0 } from 'styles/tokens';

import { NavActionItem } from '../shared/CommonNavItems';

const AddEntityDialog = (props: BaseDialogProps) => {
  const { t } = translate;
  const { hasPermissions } = useUserActions();
  const isWhiteLabelScoped = useIsWhiteLabelScoped();
  const user = useCurrentUser();
  const { scope } = user;
  const { toggleTier } = useCreateModify();
  const router = useRouter();

  const { data: userFeatureBundlesData } = useQuery(userFeatureBundlesQuery);

  const leadRedirectCallback = useCallback(
    async (step: Step, data: Record<string, any>) => {
      if (step.id !== LeadActivityBuilderStep.DETAILS) {
        return;
      }
      const leadActivityData = data as LeadActivityDetailsContainerQuery['item'];
      const isLeadsRoute = isWhiteLabelScoped || leadActivityData?.status === LeadActivityStatus.PROCESSED;
      const redirectBaseUrl = isLeadsRoute ? RoutePath.LEADS : RoutePath.LEAD_ACTIVITIES;
      const searchParameters = router.location.pathname.includes(redirectBaseUrl) ? router.location.search : '';
      const id = isLeadsRoute ? leadActivityData?.leadName?.id : leadActivityData?.id;
      void router.push(`${redirectBaseUrl}/${id}${searchParameters}`);
    },
    [isWhiteLabelScoped, router]
  );

  const entityList = useMemo(
    () =>
      [
        {
          id: BuilderType.RETAIL_ITEMS_CREATE,
          label: t('create_retail_item'),
          onClick: () => {
            const { onCancel } = props;
            onCancel?.();
            toggleTier(CreateModifyTiers.TIER_0, {
              entityType: EntityType.RETAIL_ITEM,
              mode: 'create',
              redirectBaseUrl: RoutePath.RETAIL_ITEMS,
              title: t('create_retail_item'),
              type: BuilderType.RETAIL_ITEMS_CREATE,
            });
          },
          restricted: !isFeatureBuilderEnabledForUser({
            builderType: BuilderType.RETAIL_ITEMS_CREATE,
            feature: FeatureBundleSet.RETAIL,
            hasPermissions,
            userRooftops: userFeatureBundlesData?.user?.rooftops,
          }),
          testId: ElementTestId.NAV_ENTITY_CREATE_RETAIL_ITEM,
        },
        {
          id: BuilderType.TRADE_IN_ITEMS_CREATE,
          label: t('create_trade_in'),
          onClick: () => {
            const { onCancel } = props;
            onCancel?.();
            toggleTier(CreateModifyTiers.TIER_0, {
              entityType: EntityType.TRADE_IN_ITEM,
              mode: 'create',
              redirectBaseUrl: RoutePath.TRADE_IN_ITEMS,
              title: t('create_trade_in'),
              type: BuilderType.TRADE_IN_ITEMS_CREATE,
            });
          },
          restricted: !isFeatureBuilderEnabledForUser({
            builderType: BuilderType.TRADE_IN_ITEMS_CREATE,
            feature: FeatureBundleSet.TRADE_IN,
            hasPermissions,
            userRooftops: userFeatureBundlesData?.user?.rooftops,
          }),
          testId: ElementTestId.NAV_ENTITY_CREATE_TRADE_IN,
        },
        {
          id: BuilderType.LEAD_ACTIVITY_CREATE,
          label: t('create_lead_log_activity'),
          onClick: () => {
            const { onCancel } = props;
            onCancel?.();
            toggleTier(CreateModifyTiers.TIER_0, {
              entityType: EntityType.LEAD_ACTIVITY,
              mode: 'create',
              onStepSave: leadRedirectCallback,
              title: t('log_lead_activity'),
              type: BuilderType.LEAD_ACTIVITY_CREATE,
            });
          },
          restricted: !isFeatureBuilderEnabledForUser({
            builderType: BuilderType.LEAD_ACTIVITY_CREATE,
            feature: FeatureBundleSet.LEAD,
            hasPermissions,
            userRooftops: userFeatureBundlesData?.user?.rooftops,
          }),
          testId: ElementTestId.NAV_ENTITY_LOG_LEAD_ACTIVITY,
        },
        {
          id: BuilderType.TASK_CREATE,
          label: t('create_task'),
          onClick: () => {
            const { onCancel } = props;
            onCancel?.();
            toggleTier(CreateModifyTiers.TIER_0, {
              entityType: EntityType.TASK,
              mode: 'create',
              redirectBaseUrl: RoutePath.TASKS,
              title: t('create_task'),
              type: BuilderType.TASK_CREATE,
            });
          },
          restricted: !isFeatureBuilderEnabledForUser({
            builderType: BuilderType.TASK_CREATE,
            feature: FeatureBundleSet.TASK,
            hasPermissions,
            userRooftops: userFeatureBundlesData?.user?.rooftops,
          }),
          testId: ElementTestId.NAV_ENTITY_CREATE_TASK,
        },
        {
          id: BuilderType.APPOINTMENT_CREATE,
          label: t('create_appointment'),
          onClick: () => {
            const { onCancel } = props;
            onCancel?.();
            toggleTier(CreateModifyTiers.TIER_0, {
              entityType: EntityType.APPOINTMENT,
              mode: 'create',
              redirectBaseUrl: RoutePath.APPOINTMENTS,
              title: t('create_appointment'),
              type: BuilderType.APPOINTMENT_CREATE,
            });
          },
          restricted: !isFeatureBuilderEnabledForUser({
            builderType: BuilderType.APPOINTMENT_CREATE,
            feature: FeatureBundleSet.APPOINTMENT,
            hasPermissions,
            userRooftops: userFeatureBundlesData?.user?.rooftops,
          }),
          testId: ElementTestId.NAV_ENTITY_CREATE_APPOINTMENT,
        },
        {
          id: BuilderType.ROOFTOPS_CREATE,
          label: t('create_rooftop'),
          onClick: () => {
            const { onCancel } = props;
            onCancel?.();
            toggleTier(CreateModifyTiers.TIER_0, {
              entityType: CustomEntity.ROOFTOP,
              mode: 'create',
              redirectBaseUrl: RoutePath.ROOFTOPS,
              title: t('create_rooftop'),
              type: BuilderType.ROOFTOPS_CREATE,
            });
          },
          restricted:
            !hasPermissions(builderPermissions[BuilderType.ROOFTOPS_CREATE]) ||
            // Partial group users are not able to create new rooftops
            scope === UserScope.PARTIAL_GROUP,
          testId: ElementTestId.NAV_ENTITY_CREATE_ROOFTOP,
        },
        {
          id: BuilderType.USER_CREATE,
          label: t('create_user'),
          onClick: () => {
            const { onCancel } = props;
            onCancel?.();
            toggleTier(CreateModifyTiers.TIER_0, {
              entityType: CustomEntity.USER,
              mode: 'create',
              redirectBaseUrl: RoutePath.USERS,
              title: t('create_user'),
              type: BuilderType.USER_CREATE,
            });
          },
          restricted: !hasPermissions(builderPermissions[BuilderType.USER_CREATE]),
          testId: ElementTestId.NAV_ENTITY_CREATE_USER,
        },
        {
          id: BuilderType.WEBSITE_CREATE,
          label: t('create_website'),
          onClick: () => {
            const { onCancel } = props;
            onCancel?.();
            toggleTier(CreateModifyTiers.TIER_0, {
              entityType: EntityType.WEBSITE,
              mode: 'create',
              redirectBaseUrl: RoutePath.WEBSITES,
              title: t('create_website'),
              type: BuilderType.WEBSITE_CREATE,
            });
          },
          restricted:
            !hasPermissions(builderPermissions[BuilderType.WEBSITE_CREATE]) || !fullGroupOrAboveScopes.includes(scope),
          testId: ElementTestId.NAV_ENTITY_CREATE_WEBSITE,
        },
      ].filter(item => !item.restricted),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- user dep ensures re-eval when permissions change
    [userFeatureBundlesData?.user?.rooftops, hasPermissions, leadRedirectCallback, props, t, toggleTier, scope, user]
  );

  return (
    <FilterableListDialog
      headerTitle={t('actions')}
      isFixedToTop
      listItems={entityList}
      searchPlaceholderText={t('search_actions')}
      {...props}
    />
  );
};

const AddEntityNavComponent = () => {
  const { preloadBuilders } = useCreateModifyActions();
  const [isAddEntityDialogOpen, setIsAddEntityDialogOpen] = useState<boolean>(false);

  return (
    <>
      <NavActionItem
        data-testid={ElementTestId.GLOBAL_NAV_ACTION_BUTTON}
        onClick={useCallback(() => setIsAddEntityDialogOpen(true), [])}
        onFocus={() => preloadBuilders('focus')}
        onPointerDown={() => preloadBuilders('pointerdown')}
        onPointerEnter={() => preloadBuilders('pointerenter')}
        style={{ background: GREEN_500 }}
      >
        <AddIcon color={NEUTRAL_0} />
      </NavActionItem>
      <AddEntityDialog
        isOpen={isAddEntityDialogOpen}
        onCancel={useCallback(() => setIsAddEntityDialogOpen(false), [])}
      />
    </>
  );
};

export default AddEntityNavComponent;
